import clsx from 'clsx';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import { FadeIn } from 'components/common/FadeIn';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import Arrow from '../../svg/Arrows/drawn-arrow-down.svg';
import styles from './HowItWorks.module.css';

const LazyLottieAnimation = dynamic(() => import('./lottie-animation'), {
  ssr: false,
});

interface AnimationData {
  [key: string]: unknown;
}

const HowItWorks = () => {
  const [visibleAnimations, setVisibleAnimations] = useState<{
    cage: boolean;
    prizes: boolean;
  }>({ cage: false, prizes: false });

  const [loadedAnimations, setLoadedAnimations] = useState<{
    cage: AnimationData | null;
    prizes: AnimationData | null;
  }>({ cage: null, prizes: null });

  const cageBallsRef = useRef<HTMLDivElement | null>(null);
  const prizesRef = useRef<HTMLDivElement | null>(null);

  const loadAnimationData = async (type: 'cage' | 'prizes') => {
    try {
      const data =
        type === 'cage'
          ? await import('./cage-balls.json')
          : await import('./prizes-pop-up.json');

      setLoadedAnimations((prev) => ({
        ...prev,
        [type]: data.default || data,
      }));
    } catch (error) {
      console.error(`Failed to load ${type} animation:`, error);
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          if (entry.target === cageBallsRef.current) {
            setVisibleAnimations((prev) => ({ ...prev, cage: true }));
            loadAnimationData('cage');
          } else if (entry.target === prizesRef.current) {
            setVisibleAnimations((prev) => ({ ...prev, prizes: true }));
            loadAnimationData('prizes');
          }

          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.6 },
    );

    if (cageBallsRef.current) observer.observe(cageBallsRef.current);
    if (prizesRef.current) observer.observe(prizesRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <AboutUsModule background="grey">
      <AboutUsSiteContainer styles2026>
        <div className={styles.howItWorks}>
          <FadeIn onScroll delay={0.05} className={styles.introHeading}>
            <div className={styles.headingContainer}>
              <div className={styles.headingText}>
                <p className="daymadeTypographyTaglineAccent">
                  In 3 simple steps
                </p>
                <h2 className={'daymadeTypographyXL'}>How it works</h2>
              </div>
            </div>
          </FadeIn>
          <div className={styles.stepsWrapper}>
            <FadeIn
              onScroll
              className={clsx(styles.step, styles.step1)}
              delay={0.1}
            >
              <div className={styles.innerStep}>
                <div className={styles.cageBalls} ref={cageBallsRef}>
                  {loadedAnimations.cage ? (
                    <LazyLottieAnimation
                      animationData={loadedAnimations.cage}
                      shouldPlay={visibleAnimations.cage}
                    />
                  ) : null}
                </div>
                <div className={clsx(styles.arrow1)}>
                  <Arrow />
                </div>
                <div
                  className={clsx('daymadeTypographyMBlack', styles.stepTitle)}
                >
                  Pick your numbers
                </div>
                <div
                  className={clsx(
                    'daymadeTypographyMRegular',
                    styles.stepDescription,
                  )}
                >
                  Choose your lucky numbers and which days you want to play
                </div>
              </div>
            </FadeIn>

            <FadeIn
              onScroll
              delay={0.35}
              className={clsx(styles.step, styles.step2)}
            >
              <div className={styles.innerStep}>
                <div className={clsx(styles.arrow2)}>
                  <Arrow />
                </div>
                <div
                  className={clsx('daymadeTypographyMBlack', styles.stepTitle)}
                >
                  Match to Win
                </div>
                <div
                  className={clsx(
                    'daymadeTypographyMRegular',
                    styles.stepDescription,
                  )}
                >
                  The more numbers you match, the bigger the prize
                </div>
              </div>
            </FadeIn>

            <FadeIn
              onScroll
              delay={0.6}
              className={clsx(styles.step, styles.step3)}
            >
              <div className={styles.innerStep}>
                <div className={styles.prizesPopUp} ref={prizesRef}>
                  {loadedAnimations.prizes ? (
                    <LazyLottieAnimation
                      animationData={loadedAnimations.prizes}
                      shouldPlay={visibleAnimations.prizes}
                      delay={600}
                    />
                  ) : null}
                </div>
                <div
                  className={clsx('daymadeTypographyMBlack', styles.stepTitle)}
                >
                  Choose Your Prize
                </div>
                <div
                  className={clsx(
                    'daymadeTypographyMRegular',
                    styles.stepDescription,
                  )}
                >
                  From bucket list holidays to little treats that make your day
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </AboutUsSiteContainer>
    </AboutUsModule>
  );
};

export default HowItWorks;
